<style scoped lang="scss">
    .modal-box {
        background-color: #fff;
    }
    form {
        h2 {
            color: #000;
        }
        ::v-deep(.formkit-label), ::v-deep(.formkit-option), ::v-deep(legend) {
            color: #000 !important;
            font-weight: 400;
        }
        ::v-deep(.formkit-label) {
            margin-bottom: 15px;
        }
        ::v-deep(.formkit-help) {
            font-style: italic;
            margin-top: 15px;
        }
        ::v-deep(.formkit-option) {
            display: flex;
            align-items: center;
        }
    }
</style>
<template>
    <dialog ref="HTMLDialogElement" :id="addBackOfficeUserFormModalid" class="modal modal-middle sm:modal-middle">
        <div class="modal-box" method="dialog">
            <FormKit
                type="form"
                id="addBackOfficeUserForm"
                @submit="submitHandler"
                :actions="false"
                incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                #default="{ state: { valid } }"
            >
                <h2>Modifier un utilisateur</h2>
                <hr />
                <FormKit
                    v-model="selectedUser"
                    name="backOfficeUser"
                    type="autocomplete"
                    label="Utilisateur"
                    placeholder="Choisissez un utilisateur"
                    help="Veuillez renseignez au minimum 3 lettres"
                    selection-appearance="option"
                    empty-message="Aucun résultat"
                    validation="required"
                    validation-visibility="dirty"
                    :validation-messages="{ required: 'Ce champs est requis.' }"
                    :options="setUsersListForDropdown"
                />
                <div class="modal-action">
                    <button type="button" class="btn" @click="closeModalDialog()">Annuler</button>
                    <FormKit type="submit" label="Modifier" :disabled="!valid" />
                </div>
            </FormKit>
        </div>
    </dialog>
</template>

<script lang="ts" setup>
import { ref, onMounted, toRef } from "vue";
import {
    getUsersNotLinkedToBackOfficeSupplier,
    addExistingUserToBackOfficeList,
    IUserListToForDropdown,
    IUpdateBackOfficeUserList
} from "@/_services/supplier/backOfficeSupplier/backofficeSupplier";
import swal from "@/_helpers/swal";

const props = defineProps({
    supplierUuid: {
        type: String,
        required: false,
        default: "",
    },
    addBackOfficeUserFormModalid: {
        type: String,
        required: true,
    },
});
const addBackOfficeUserFormModalid = toRef(props, "addBackOfficeUserFormModalid");
const modalHTMLElement = ref<HTMLDialogElement | null>(null);
const supplierUuid = toRef(props, "supplierUuid");
const requiredLengthToAutoComplete: number = 3 as number;
const selectedUser = ref<string | null>(null);

async function setUsersListForDropdown({ search }) {
    if (!search) return [];
    if (search.length >= requiredLengthToAutoComplete) {
        // Iterating over results to set the required `label` and `value` keys.
        const response = await getUsersNotLinkedToBackOfficeSupplier(search, supplierUuid.value);
        return response.map((result: IUserListToForDropdown) => {
            return {
                label: result.fullname,
                value: result.uuid
            }
        })
    }
    return []; // If the request fails, we return an empty array.
}


const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
        if (mutation.attributeName === "open" && modalHTMLElement.value) {
            const isOpen = modalHTMLElement.value.open;
            const isclosed = !isOpen;
            if (isclosed) {
                selectedUser.value = null;
            }
            break; // Exit the loop once the class attribute has been processed
        }
    }
});

onMounted(() => {
    modalHTMLElement.value = document.querySelector(
        `#${addBackOfficeUserFormModalid.value}`
    );
    if (modalHTMLElement.value) {
        observer.observe(modalHTMLElement.value, { attributes: true });
    }
});

const submitHandler = async (formData, formNode) => {
    try {
        closeModalDialog();
        const backOfficeUserUuid = formData.backOfficeUser;
        const data: IUpdateBackOfficeUserList = {
            user: `api/users/${backOfficeUserUuid}`,
            supplier: `api/suppliers/${supplierUuid.value}`
        };
        await swal
            .confirm("Êtes-vous sûr de vouloir rajouter cet utilisateur au back office de ce fournisseur ?",
                "Annuler",
                "Oui"
            )
            .then((results) => results.isConfirmed && addUserToBackOfficeList(data));
    } catch (err: any) {
        swal.error(err.errors[0]);
    }
};

const addUserToBackOfficeList = (data: IUpdateBackOfficeUserList) => {
    addExistingUserToBackOfficeList(data);
};

const closeModalDialog = () => {
    if (modalHTMLElement.value) {
        modalHTMLElement.value.close();
    }
};
</script>